/* eslint-disable no-unused-vars */
import { select, takeLatest, put, call, actionChannel, take, all } from 'redux-saga/effects';
import { message } from 'antd';
import get from 'lodash/get';
import { deletePersistence, getPersistence, putPersistence } from '@src/api/persistence';
import instance from '@src/api/axiosInstance';
import * as commonTypes from '@store/common/types';
import {
  getPersistentHashKey,
  getKeyToEventMap,
  trendsRouteKey,
  aeRouteKey as ecoTrendsRouteKey,
  nocRouteKey,
  mlRouteKey,
  ovbRouteKey,
  checkValidPresetPersistenceKey,
  checkValidPersistenceKey,
} from '@utils/persistenceUtil';
import * as nocBaseTypes from '@store/noc/base/types';
import { selector as commonSelector } from '@store/common';
import { NO_COMPARE_MODE } from '@utils/constants';
import * as ovbBaseTypes from '@store/overviewDashboard/base/types';
import * as mlBaseTypes from '@store/metricLens/base/types';
import * as vmBaseTypes from '@store/viewerModule/base/types';
import persistenceSelector, { persistenceResponseSelector } from './selectors';
import * as types from './types';
import { removeLoading, setActivePersistenceKey } from './actions';
import {
  updateFiltersForTrends,
  updateFiltersForNOC,
  updateFiltersForMetricLens,
  updateFiltersForOverview,
} from './utils';
import { DEFAULT_PERSISTENCE_STATE } from './reducer';

function* sagaGetUrlPersistence(action) {
  /* No persistence for compareMode */
  const { compareMode } = yield select(commonSelector);
  if (compareMode !== NO_COMPARE_MODE) return;

  // get the url of current page
  const key = getPersistentHashKey();

  yield put(setActivePersistenceKey(key));

  const persistenceDoneEvent = getKeyToEventMap(key);
  const persistenceResponse = yield select(persistenceResponseSelector);
  const { isFetched } = persistenceResponse[key] ?? DEFAULT_PERSISTENCE_STATE;

  try {
    if (!isFetched) {
      const result = yield call(
        getPersistence,
        {
          key,
        },
        instance,
        {
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
          },
        },
      );
      // Extract data from axios response
      const response = result.data;
      try {
        const configKey: string = response.key;
        if (
          [trendsRouteKey, ecoTrendsRouteKey].includes(configKey) ||
          checkValidPresetPersistenceKey(configKey)
        ) {
          yield call(updateFiltersForTrends, response);
        } else if ([nocRouteKey].includes(configKey)) {
          yield call(updateFiltersForNOC, response);
        } else if ([mlRouteKey].includes(configKey)) {
          yield call(updateFiltersForMetricLens, response);
        } else if ([ovbRouteKey].includes(configKey)) {
          yield call(updateFiltersForOverview, response);
        }
      } catch (e) {
        // do nothing
      }
      yield all([
        put({ type: types.GET_URL_PERSISTENT_CONFIGURATION_SUCCESS, response }),
        put({ type: persistenceDoneEvent, response, meta: action }),
      ]);
    } else {
      const { response } = persistenceResponse[key];
      yield put({ type: persistenceDoneEvent, response, meta: action });
      yield put(removeLoading(key));
    }
  } catch (error: any) {
    // Handle 404 as success with empty data (no persistence saved yet)
    if (error?.response?.status === 404) {
      const response = { key };
      yield all([
        put({ type: types.GET_URL_PERSISTENT_CONFIGURATION_SUCCESS, response }),
        put({ type: persistenceDoneEvent, response, meta: action }),
      ]);
      return;
    }

    // to keep structure consistent for listeners
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to load persistence';
    const res = {
      errorMessage,
      response: { key },
      meta: action,
    };
    yield put({ type: types.GET_URL_PERSISTENT_CONFIGURATION_FAIL, ...res });
    yield put({ type: persistenceDoneEvent, ...res });
  }
}

function* sagaUpdateUrlConfiguration(action) {
  const { isSharingPage } = yield select(commonSelector);
  if (isSharingPage) {
    return;
  }

  const persistenceStore = yield select(persistenceSelector);
  const key = get(persistenceStore, ['key'], null) || getPersistentHashKey();

  if (!checkValidPersistenceKey(key)) return;

  const { configuration } = action;

  try {
    if (key) {
      const result = yield call(putPersistence, { key, configuration }, instance);
      // Extract data from axios response
      const response = result.data;
      yield put({
        type: types.UPDATE_URL_PERSISTENT_CONFIGURATION_SUCCESS,
        response,
      });
    }
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to update persistence';
    message.error(errorMessage);
    yield put({
      type: types.UPDATE_URL_PERSISTENT_CONFIGURATION_FAIL,
      errorMessage,
    });
  }
}

function* sagaDeletePersistence() {
  const persistenceStore = yield select(persistenceSelector);
  const key = get(persistenceStore, ['key'], null) || getPersistentHashKey();

  try {
    if (key) {
      yield call(deletePersistence, key, instance);
      window.location.reload();
    }
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.message ||
      'Failed to delete persistence';
    message.error(errorMessage);
  }
}

function* watchGetUrlPersistence() {
  yield takeLatest([types.GET_URL_PERSISTENT_CONFIGURATION], sagaGetUrlPersistence);
}

function* watchUpdateUrlConfiguration() {
  // channel to queue persistence updates
  const persistenceChannel = yield actionChannel([
    types.UPDATE_URL_PERSISTENT_CONFIGURATION,
    commonTypes.PERSIST_STATE, // Trends Persistence
    nocBaseTypes.PERSIST_STATE, // NOC default dashboard persistence
    ovbBaseTypes.PERSIST_STATE, // overview dashboard
    mlBaseTypes.PERSIST_STATE, // ML Dashboard
    vmBaseTypes.PERSIST_STATE, // VM Dashboard
  ]);
  while (true) {
    // run one by one to avoid key misses
    const action = yield take(persistenceChannel);
    yield sagaUpdateUrlConfiguration(action);
  }
}

function* watchDeletePersistence() {
  yield takeLatest([types.DELETE_URL_PERSISTENT_CONFIGURATION], sagaDeletePersistence);
}

export default [watchGetUrlPersistence, watchUpdateUrlConfiguration, watchDeletePersistence];
